import React, { Fragment } from 'react';
import { IconButton, Badge, Typography, AppBar, Toolbar } from '@material-ui/core';
import PropTypes from 'prop-types';
import { ROLES, GLOBAL_NAVIGATION_TABS } from 'constants';
import { useMutation, useSubscription } from 'react-apollo-hooks';
import { getNotificationsCounter } from 'utils/commonUtils';
import { useConversationSubscribe } from 'shell/hooks/useConversationSubscribe';
import {
  Conversations,
  ConversationsSelected,
  GlobalSearch,
  GlobalSearchSelected,
  HomeIcon,
  HomeIconSelected,
  Alerts,
  AlertsSelected,
  MoreVerticalSelected,
  MoreVertical,
  ClientsSelectedIcon,
  ClientsIcon2,
  StyleIcon,
  StyleSelectedIcon,
} from 'sharedComponents/Icons';
import { useCalendarRange } from 'shell/hooks/useCalendarRange';
import MoreMenu from '../MoreMenu';
import { markNotificationRead } from '../../../../graphQL/mutation';
import {
  SubscribeToAssociateMessageNotificationCreated,
  SubscribeToAssociateMessageNotificationUpdated,
  SubscribeToAssociateMessageNotificationRead,
  SubscribeToCreateNotification,
  SubscribeToUpdatedNotificationCount,
  SubscribeToReadNotificationCounts,
} from '../../../../graphQL/subscription';

const getOptions = (associateId, subscription, cb) => ({
  variables: { associateId },
  shouldResubscribe: true,
  onSubscriptionData: ({ subscriptionData: { data } }) => {
    if (!data) {
      return null;
    }
    return cb(data[subscription]);
  },
});

const MessageSubscribe = ({ associateId, handler, setError }) => {
  const { error: createdError } = useSubscription(
    SubscribeToAssociateMessageNotificationCreated,
    getOptions(associateId, 'onAssociateMessageNotificationCreated', handler),
  );

  const { error: addedError } = useSubscription(
    SubscribeToAssociateMessageNotificationUpdated,
    getOptions(associateId, 'onAssociateMessageNotificationUpdated', handler),
  );

  const { error: readError } = useSubscription(
    SubscribeToAssociateMessageNotificationRead,
    getOptions(associateId, 'onAssociateMessageNotificationRead', handler),
  );

  React.useEffect(() => setError(addedError), [addedError]);
  React.useEffect(() => setError(createdError), [createdError]);
  React.useEffect(() => setError(readError), [readError]);

  return null;
};

const AlertsSubscribe = ({ associateId, handler, setError }) => {
  const { error: createdError } = useSubscription(
    SubscribeToCreateNotification,
    getOptions(associateId, 'onNotificationCountCreated', handler),
  );

  const { error: updatedError } = useSubscription(
    SubscribeToUpdatedNotificationCount,
    getOptions(associateId, 'onNotificationCountUpdated', handler),
  );

  const { error: readError } = useSubscription(
    SubscribeToReadNotificationCounts,
    getOptions(associateId, 'onNotificationCountRead', handler),
  );

  React.useEffect(() => setError(createdError), [createdError]);
  React.useEffect(() => setError(updatedError), [updatedError]);
  React.useEffect(() => setError(readError), [readError]);

  return null;
};

const GlobalNav = ({
  classes,
  history,
  location,
  user,
  isVisible,
  isHomePageEnabled,
  isChatEnabled,
  notifications,
  refetchMessageCount,
  alertNotifications,
  closeStylyzeApp,
  isStatefulGlobalNav,
  isBackOfHouseUser,
}) => {
  const [subscriptionError, setError] = useConversationSubscribe();
  const [menuIsOpen, setMenuIsOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('more');
  const [postMutation] = useMutation(markNotificationRead);
  const [messageNotifications, setMessageNotifications] = React.useState(notifications);
  const [alertNotification, setAlertNotification] = React.useState(alertNotifications);
  const { resetDate } = useCalendarRange();
  const updateMessages = (newNotification) => {
    const restMessages = messageNotifications.filter((message) => message.contactId !== newNotification.contactId);
    setMessageNotifications([newNotification, ...restMessages]);
  };

  const updateAlerts = (alert) => {
    setAlertNotification(alert);
  };

  React.useEffect(() => {
    setMessageNotifications(notifications);
  }, [notifications]);

  React.useEffect(() => {
    setAlertNotification(alertNotifications);
  }, [alertNotifications]);

  React.useEffect(() => {
    refetchMessageCount();
  }, [isVisible]);

  React.useEffect(() => {
    const currentTab = Object.entries(GLOBAL_NAVIGATION_TABS).find(([, value]) =>
      location.pathname.startsWith(value.path),
    );

    const [key] = currentTab || ['more'];

    setActiveTab(key);
    setMenuIsOpen(false);
  }, [location.pathname]);

  const onMoreIconClick = () => setMenuIsOpen(!menuIsOpen);

  const onClick = async (tabName, postMutation) => {
    await closeStylyzeApp();
    setMenuIsOpen(false);
    if (tabName === 'alerts') {
      postMutation({
        variables: { associateId: user.employeeNumber },
        update: () => {
          history.push(GLOBAL_NAVIGATION_TABS.alerts.path);
        },
      });
    }
    if (tabName === 'messages') {
      refetchMessageCount();
    }

    if (tabName !== activeTab && tabName !== 'alerts') {
      return history.push(GLOBAL_NAVIGATION_TABS[tabName].path);
    }

    // Return user to root location of active module
    // Specially for AC-4 of https://jira.mynmg.com/browse/OSP-5285
    if (tabName === activeTab) {
      history.replace(GLOBAL_NAVIGATION_TABS[tabName].path, {
        resetEntries: true,
      });
    }
    // Reset Performance context provider (CalendarRange) to initial state
    // when clicking 'Home' while in Home module
    if (tabName === 'home' && activeTab === 'home') {
      resetDate();
    }
  };

  const stylist = user.role === ROLES.STYLIST || user.role === ROLES.TESTER;

  const navBarProps = [
    {
      page: 'home',
      selected: <HomeIconSelected />,
      unSelected: <HomeIcon className={classes.semiTransparent} />,
      caption: 'Home',
      toggle: isHomePageEnabled,
      onClick: () => onClick('home'),
    },
    {
      page: 'search',
      selected: <GlobalSearchSelected />,
      unSelected: <GlobalSearch className={classes.semiTransparent} />,
      caption: 'Search',
      onClick: () => onClick('search'),
      toggle: true,
    },
    {
      page: 'clients',
      selected: <ClientsSelectedIcon />,
      unSelected: <ClientsIcon2 className={classes.semiTransparent} />,
      caption: 'Clients',
      toggle: isStatefulGlobalNav,
      onClick: () => onClick('clients'),
      hidden: isBackOfHouseUser,
    },
    {
      page: 'style',
      selected: <StyleSelectedIcon />,
      unSelected: <StyleIcon className={classes.semiTransparent} />,
      caption: 'Style',
      toggle: isStatefulGlobalNav,
      onClick: () => onClick('style'),
    },
    {
      page: 'messages',
      selected: <ConversationsSelected />,
      unSelected: (
        <Badge
          badgeContent={getNotificationsCounter(messageNotifications)}
          color="primary"
          classes={{ badge: classes.customBadge }}
        >
          <Conversations className={classes.semiTransparent} />
        </Badge>
      ),
      caption: 'Messages',
      toggle: isChatEnabled,
      onClick: () => onClick('messages'),
      hidden: isBackOfHouseUser,
    },
    {
      page: 'alerts',
      selected: <AlertsSelected />,
      unSelected: (
        <Badge
          badgeContent={alertNotification && alertNotification.notificationCount}
          color="primary"
          classes={{ badge: classes.customBadge }}
        >
          <Alerts className={classes.semiTransparent} />
        </Badge>
      ),
      toggle: !isStatefulGlobalNav,
      onClick: () => onClick('alerts', postMutation),
    },
    {
      page: 'more',
      selected: <MoreVerticalSelected />,
      unSelected: <MoreVertical className={classes.semiTransparent} />,
      toggle: !isStatefulGlobalNav,
      onClick: () => onMoreIconClick(),
    },
  ];
  const activeNavBarProps = navBarProps.filter(({ hidden }) => !hidden);

  return (
    <Fragment>
      {isVisible && stylist ? (
        <Fragment>
          <MoreMenu open={menuIsOpen} onClose={onMoreIconClick} user={user} />
          <AppBar position="fixed" color="primary" className={classes.appBar}>
            <Toolbar className={classes.toolbar}>
              {activeNavBarProps.map((item) => {
                const { page, selected, unSelected, caption, toggle, onClick } = item;
                return (
                  <Fragment key={page}>
                    {toggle && (
                      <IconButton
                        aria-label={page !== 'more' ? `${page}page` : 'moreicon'}
                        data-testid={page !== 'more' ? `${page}-page` : 'moreIcon'}
                        onClick={onClick}
                        classes={{ label: classes.iconLabel }}
                      >
                        {page !== 'more' && (activeTab === page && !menuIsOpen ? selected : unSelected)}
                        {page === 'more' && (menuIsOpen ? selected : unSelected)}
                        {isStatefulGlobalNav && (
                          <Typography variant="caption" color={activeTab === page ? 'textPrimary' : 'textSecondary'}>
                            {caption}
                          </Typography>
                        )}
                      </IconButton>
                    )}
                  </Fragment>
                );
              })}
            </Toolbar>
          </AppBar>
          <div className={classes.offset} />
        </Fragment>
      ) : null}
      {!subscriptionError && (
        <Fragment>
          {isChatEnabled && (
            <MessageSubscribe associateId={user.employeeNumber} handler={updateMessages} setError={setError} />
          )}
          <AlertsSubscribe associateId={user.employeeNumber} handler={updateAlerts} setError={setError} />
        </Fragment>
      )}
    </Fragment>
  );
};

GlobalNav.propTypes = {
  user: PropTypes.shape({
    role: PropTypes.string,
    employeeNumber: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    logged: PropTypes.bool.isRequired,
  }),
  classes: PropTypes.objectOf(PropTypes.string),
  location: PropTypes.shape({}),
  isVisible: PropTypes.bool,
  isHomePageEnabled: PropTypes.bool,
  isChatEnabled: PropTypes.bool,
  refetchMessageCount: PropTypes.func,
  isStatefulGlobalNav: PropTypes.bool,
};

GlobalNav.defaultProps = {
  classes: {},
  user: {
    role: '',
    employeeNumber: '',
    firstName: '',
    lastName: '',
    logged: false,
  },
  location: {},
  isVisible: true,
  isHomePageEnabled: false,
  isChatEnabled: false,
  refetchMessageCount: () => null,
  isStatefulGlobalNav: false,
};

export default GlobalNav;
